// Utility for persisting podcast playback timestamps in localStorage
// Remembers where users left off listening to each podcast

export type PodcastTimestamp = {
  podcastId: string
  currentTime: number // in seconds
  duration: number // in seconds
  lastUpdated: number // epoch ms
}

const STORAGE_KEY = 'podcast_playback_timestamps'
const MAX_TIMESTAMPS = 50 // Limit storage to most recent 50 podcasts

function isPodcastTimestamp(v: unknown): v is PodcastTimestamp {
  return v !== null && 
         typeof v === 'object' && 
         'podcastId' in v && 
         'currentTime' in v && 
         'duration' in v &&
         'lastUpdated' in v &&
         typeof (v as PodcastTimestamp).podcastId === 'string' &&
         typeof (v as PodcastTimestamp).currentTime === 'number' &&
         typeof (v as PodcastTimestamp).duration === 'number' &&
         typeof (v as PodcastTimestamp).lastUpdated === 'number'
}

function readStore(): Record<string, PodcastTimestamp> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null) return {}
    const result: Record<string, PodcastTimestamp> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (isPodcastTimestamp(value)) {
        result[key] = value
      }
    }
    return result
  } catch {
    return {}
  }
}

function writeStore(timestamps: Record<string, PodcastTimestamp>) {
  try {
    // Keep only the most recent MAX_TIMESTAMPS entries
    const entries = Object.values(timestamps)
      .sort((a, b) => b.lastUpdated - a.lastUpdated)
      .slice(0, MAX_TIMESTAMPS)
    
    const pruned: Record<string, PodcastTimestamp> = {}
    for (const entry of entries) {
      pruned[entry.podcastId] = entry
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned))
  } catch {
    // ignore storage errors
  }
}

/**
 * Get the saved timestamp for a podcast, if it exists
 */
export function getPodcastTimestamp(podcastId: string): number | null {
  const timestamps = readStore()
  const entry = timestamps[podcastId]
  if (!entry) return null
  
  // Don't restore if we're at the very end (within last 5 seconds)
  if (entry.duration > 0 && entry.currentTime >= entry.duration - 5) {
    return null
  }
  
  // Don't restore if at the very beginning (first 3 seconds)
  if (entry.currentTime < 3) {
    return null
  }
  
  return entry.currentTime
}

/**
 * Save the current playback position for a podcast
 */
export function savePodcastTimestamp(
  podcastId: string,
  currentTime: number,
  duration: number
): void {
  if (!podcastId || !isFinite(currentTime) || !isFinite(duration)) return
  if (currentTime < 0 || duration <= 0) return
  
  const timestamps = readStore()
  timestamps[podcastId] = {
    podcastId,
    currentTime,
    duration,
    lastUpdated: Date.now(),
  }
  writeStore(timestamps)
}

/**
 * Clear the saved timestamp for a podcast (e.g., when finished)
 */
export function clearPodcastTimestamp(podcastId: string): void {
  const timestamps = readStore()
  delete timestamps[podcastId]
  writeStore(timestamps)
}
